import { useState } from 'react'
import { 
  Card, 
  CardContent, 
  CardHeader, 
  CardTitle,
  CardDescription
} from "@/components/ui/card"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { useToast } from "@/hooks/use-toast"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog"
import { Plus, Trash, Pencil, Check, X } from 'lucide-react'
import { useUsers } from '@/hooks/users/useUsers'
import type { User } from '@/types/user'

export default function UsersPage() {
  const [newUserId, setNewUserId] = useState('')
  const [editingUser, setEditingUser] = useState<string | null>(null)
  const [editValue, setEditValue] = useState('')
  const { toast } = useToast()
  
  const { 
    users, 
    isLoading,
    error,
    createUser,
    updateUser,
    deleteUser
  } = useUsers()

  if (error) {
    toast({
      title: "Error",
      description: "Failed to fetch users.",
      variant: "destructive"
    })
  }

  const handleCreate = (e: React.FormEvent) => {
    e.preventDefault()
    if (newUserId.trim()) {
      createUser.mutate(newUserId.trim(), {
        onSuccess: () => {
          setNewUserId('')
          toast({ title: "Success", description: "User created successfully." })
        },
        onError: () => {
          toast({
            title: "Error",
            description: "Failed to create user.",
            variant: "destructive"
          })
        }
      })
    }
  }

  const handleStartEdit = (user: User) => {
    setEditingUser(user.user_id)
    setEditValue(user.user_id)
  }

  const handleSaveEdit = () => {
    if (editingUser && editValue.trim() && editValue !== editingUser) {
      updateUser.mutate({
        user_id: editValue.trim()
      }, {
        onSuccess: () => {
          setEditingUser(null)
          toast({ title: "Success", description: "User updated successfully." })
        },
        onError: () => {
          toast({
            title: "Error",
            description: "Failed to update user.",
            variant: "destructive"
          })
        }
      })
    } else {
      setEditingUser(null)
    }
  }

  const handleDelete = (userId: string) => {
    deleteUser.mutate(userId, {
      onSuccess: () => {
        toast({ title: "Success", description: "User deleted successfully." })
      },
      onError: () => {
        toast({
          title: "Error",
          description: "Failed to delete user.",
          variant: "destructive"
        })
      }
    })
  }

  if (isLoading) {
    return (
      <div className="container mx-auto p-6">
        <Card>
          <CardContent className="flex justify-center items-center h-64">
            Loading...
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="container mx-auto p-6">
      <Card>
        <CardHeader>
          <CardTitle>User Management</CardTitle>
          <CardDescription>
            Add, update, or remove users from the system
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleCreate} className="flex gap-4 mb-6">
            <Input
                aria-label="User ID"
                placeholder="Enter new user ID"
                value={newUserId}
                onChange={(e) => setNewUserId(e.target.value)}
                className="max-w-sm"
                disabled={createUser.isPending}
            />
            <Button
                type="submit"
                disabled={!newUserId.trim() || createUser.isPending}
            >
              <Plus className="h-4 w-4 mr-2"/>
              Add User
            </Button>
          </form>

          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>User ID</TableHead>
                <TableHead className="w-[100px]">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {users.map((user) => (
                <TableRow key={user.user_id}>
                  <TableCell>
                    {editingUser === user.user_id ? (
                      <Input
                        value={editValue}
                        onChange={(e) => setEditValue(e.target.value)}
                        className="max-w-sm"
                        disabled={updateUser.isPending}
                      />
                    ) : (
                      user.user_id
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      {editingUser === user.user_id ? (
                        <>
                          <Button
                            size="icon"
                            variant="ghost"
                            onClick={handleSaveEdit}
                            disabled={updateUser.isPending}
                          >
                            <Check className="h-4 w-4" />
                          </Button>
                          <Button
                            size="icon"
                            variant="ghost"
                            onClick={() => setEditingUser(null)}
                            disabled={updateUser.isPending}
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </>
                      ) : (
                        <Button
                          size="icon"
                          variant="ghost"
                          onClick={() => handleStartEdit(user)}
                           aria-label="Edit User"
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                      )}
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button 
                            size="icon" 
                            variant="ghost"
                            disabled={deleteUser.isPending}
                             aria-label="Delete User"
                          >
                            <Trash className="h-4 w-4" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Delete User</AlertDialogTitle>
                            <AlertDialogDescription>
                              Are you sure you want to delete this user? This action cannot be undone.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction
                              onClick={() => handleDelete(user.user_id)}
                            >
                              Delete
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  )
}